import { envConfig } from "../../config";

/**
 * Log levels
 */

const LogLevel = {
  DEBUG: "DEBUG",
  INFO: "INFO",
  WARN: "WARN",
  ERROR: "ERROR",
} as const;

type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

/**
 * Log level colors for console
 */

const LOG_COLORS = {
  [LogLevel.DEBUG]: "#6366f1",
  [LogLevel.INFO]: "#3b82f6",
  [LogLevel.WARN]: "#f59e0b",
  [LogLevel.ERROR]: "#ef4444",
} as const;

/**
 * Base logger class
 */

class Logger {
  private enabled: boolean;

  constructor() {
    this.enabled = envConfig.features.enableLogger;
  }

  /**
   * Formats the log message with timestamp and level
   */
  private format(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    const color = LOG_COLORS[level];

    // Style the log badge
    const style = `
    background:${color};
    color:white;
    padding:2px 6px;
    border-radius:3px;
    font-weight:bold;
    `;

    console.log(
      `%c${level}%c [${timestamp}] ${message}`,
      style,
      "color:inherit",
      ...args,
    );
  }
  /**
   * Debug level logging
   * WHY: For detailed debugging information
   */
  debug(message: string, ...args: unknown[]): void {
    this.format(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Info level logging
   * WHY: For general information
   */

  info(message: string, ...args: unknown[]): void {
    this.format(LogLevel.INFO, message, ...args);
  }

  /**
   * Warning level logging
   * WHY: For warning messages
   */
  warn(message: string, ...args: unknown[]): void {
    this.format(LogLevel.WARN, message, ...args);
  }
  /**
   * Error level logging
   * WHY: For error messages
   */
  error(message: string, error?: Error | unknown, ...args: unknown[]): void {
    this.format(LogLevel.ERROR, message, error, ...args);

    // Also log stack trace for errors
    if (error instanceof Error && error.stack) {
      console.error(error.stack);
    }
  }
  /**
   * Group logging (collapsible in console)
   * WHY: Group related logs together
   */
  group(label: string, callback: () => void): void {
    if (!this.enabled) return;

    console.group(label);
    callback();
    console.groupEnd();
  }

  /**
   * Table logging
   * WHY: Display data in table format
   */
  table(data: unknown): void {
    if (!this.enabled) return;
    console.table(data);
  }
}

// export singleton instance
export const logger = new Logger();
